import * as React from 'react';

import { GlobalStateContext } from '~src/global-state';
import { useIpc } from '~src/utils/ipc-utils';

import { BillingFSMPayload, RecipientFSMPayload } from '../state-machines/shared-types';

/**
 * This context provides simple mirror of finite state machine's context
 * and additional metadata to perform side effects inside fullscreen components
 */

export const FSMContext = React.createContext<
  { recipientState: RecipientFSMPayload; billingState: BillingFSMPayload | undefined } | undefined
>(undefined);

export const FSMContextMirror: React.FC<React.PropsWithChildren> = ({ children }) => {
  const globalState = React.useContext(GlobalStateContext);
  const ipc = useIpc();

  const [recipientMirrorState, setRecipientMirrorState] = React.useState<
    RecipientFSMPayload | undefined
  >(undefined);
  const [billingMirrorState, setBillingMirrorState] = React.useState<BillingFSMPayload | undefined>(
    undefined
  );

  React.useEffect(() => {
    ipc.listen(payload => {
      if (payload.name === 'sync_recipient_fsm_state') {
        setRecipientMirrorState(payload.data);
      }

      if (payload.name === 'sync_billing_fsm_state') {
        setBillingMirrorState(payload.data);
      }
    });

    return () => ipc.detach();
  }, []);

  if (!globalState?.use_recipient_address) return <>{children}</>;

  if (
    !recipientMirrorState ||
    (globalState.address_form_configuration?.features?.show_billing_address && !billingMirrorState)
  )
    return null;

  return (
    <FSMContext.Provider
      value={{ recipientState: recipientMirrorState, billingState: billingMirrorState }}
    >
      {children}
    </FSMContext.Provider>
  );
};

export function useFSMContext() {
  const state = React.useContext(FSMContext);
  if (state !== undefined) {
    return state;
  }
  throw 'FSMContext is not yet defined, you are probably using it in a wrong place';
}
